import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { COLLECTION_ROUTE, HOME_ROUTE, MY_RECO_ROUTE, PROFILE_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { cn } from '@/lib/utils';
import {
  AccountCircleFillIcon,
  AccountCircleIcon,
  CardsStarFillIcon,
  CardsStarIcon,
  HeadphonesFillIcon,
  HeadphonesIcon,
  RocketFillIcon,
  RocketIcon,
} from '@/components/icons/TabIcons';
import RecoFab from './RecoFab';

/**
 * The bottom tab bar of Android and the web — docked under the content.
 * Découvre · Mes reco · [🎵] · Collection · Profil. The center 🎵 is an
 * elevated FAB that turns yellow while the reco flow is active.
 */

interface TabLinkProps {
  to: string;
  label: string;
  icon: ReactNode;
  /** Material 3: the selected tab shows the filled glyph. */
  activeIcon: ReactNode;
  badge?: boolean;
  onClick?: () => void;
}

function TabLink({ to, label, icon, activeIcon, badge, onClick }: TabLinkProps) {
  const testId = `tab-${to.replace('/', '')}`;
  return (
    <Link
      to={to}
      onClick={onClick}
      className="tw:relative tw:flex tw:min-h-12 tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:py-2"
      data-testid={testId}
    >
      {({ isActive }: { isActive: boolean }) => {
        const tone = isActive ? 'tw:text-white' : 'tw:text-grey-3';
        return (
          <>
            {/* android-tab-bar — Material's active indicator (64 × 32) in the iOS pill's white/10 */}
            <span
              data-testid={isActive ? `${testId}-pill` : undefined}
              className={cn(
                'tw:relative tw:flex tw:h-8 tw:w-16 tw:items-center tw:justify-center tw:rounded-full',
                isActive && 'tw:bg-white/10',
              )}
            >
              <span className={tone}>{isActive ? activeIcon : icon}</span>
              {badge && (
                <span className="tw:absolute tw:right-4 tw:top-0.5 tw:size-2 tw:rounded-full tw:bg-error" />
              )}
            </span>
            <span className={cn('tw:text-xs', tone)}>{label}</span>
          </>
        );
      }}
    </Link>
  );
}

interface Props {
  pathname: string;
  /** Tap on Découvre — the layout turns a double-tap into "feed to the top". */
  onHomeTap: () => void;
}

export default function DefaultTabBar({ pathname, onHomeTap }: Props) {
  const { t } = useTranslation();

  return (
    <nav
      className="tw:relative tw:flex tw:shrink-0 tw:items-stretch tw:justify-around tw:border-t tw:border-white/5 tw:bg-dark-1"
      // Background fills the home-indicator area (no dark gap below the bar);
      // the inset keeps the icons/labels above the indicator.
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Main navigation"
    >
      <TabLink
        to={HOME_ROUTE}
        label={t('tabs.home')}
        badge
        icon={<HeadphonesIcon size={24} />}
        activeIcon={<HeadphonesFillIcon size={24} />}
        onClick={onHomeTap}
      />
      <TabLink
        to={MY_RECO_ROUTE}
        label={t('tabs.myReco')}
        icon={<RocketIcon size={24} />}
        activeIcon={<RocketFillIcon size={24} />}
      />

      <RecoFab pathname={pathname} large />

      <TabLink
        to={COLLECTION_ROUTE}
        label={t('tabs.collection')}
        icon={<CardsStarIcon size={24} />}
        activeIcon={<CardsStarFillIcon size={24} />}
      />
      <TabLink
        to={PROFILE_ROUTE}
        label={t('tabs.profile')}
        icon={<AccountCircleIcon size={24} />}
        activeIcon={<AccountCircleFillIcon size={24} />}
      />
    </nav>
  );
}
